import './App.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import bwobMainImage from './assets/transparent-bwob.png';
import BwobMemeScroller from './BwobMemeScroller';
import { Link } from 'react-router-dom';
import {
  DEXSCREENER_URL,
  PUMP_FUN_URL,
  SOLANA_CONTRACT_ADDRESS,
} from './siteConfig';

function shortenContract(addr, head = 4, tail = 4) {
  if (!addr || addr.length <= head + tail + 3) return addr || '';
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
}

const externalIcon = (
  <svg className="contract-address-pill__icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      fill="currentColor"
      d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3zM5 5h6v2H7v10h10v-4h2v6H5V5z"
    />
  </svg>
);

function LaunchLinkPill({ label, href, ariaActive, ariaSoon }) {
  const url = href?.trim();
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="contract-address-pill contract-address-pill--link home-cta__item"
        aria-label={ariaActive}
      >
        <span className="contract-address-pill__label">{label}</span>
        <span className="contract-address-pill__value">Open</span>
        <span className="contract-address-pill__chev" aria-hidden>
          {externalIcon}
        </span>
      </a>
    );
  }
  return (
    <div className="contract-address-pill home-cta__item" title={`${label} — coming soon`}>
      <span className="contract-address-pill__label">{label}</span>
      <span className="contract-address-pill__value">Coming soon</span>
      <span className="contract-address-pill__chev">
        <button type="button" className="contract-address-pill__copy" disabled aria-label={ariaSoon}>
          {externalIcon}
        </button>
      </span>
    </div>
  );
}

function HomePage() {
  const [copied, setCopied] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');
  const [hasTyped, setHasTyped] = useState(false);
  const movementRef = useRef(null);
  const hasAddress = Boolean(SOLANA_CONTRACT_ADDRESS && SOLANA_CONTRACT_ADDRESS.trim());

  const fullText = 'Are you ready to join the\nmovement, anon?';

  const onCopyContract = useCallback(async () => {
    if (!hasAddress) return;
    try {
      await navigator.clipboard.writeText(SOLANA_CONTRACT_ADDRESS.trim());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }, [hasAddress]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTyped) {
            setHasTyped(true);
            let currentIndex = 0;
            const typeInterval = setInterval(() => {
              if (currentIndex <= fullText.length) {
                setTypewriterText(fullText.slice(0, currentIndex));
                currentIndex++;
              } else {
                clearInterval(typeInterval);
              }
            }, 45);
            return () => clearInterval(typeInterval);
          }
        });
      },
      { threshold: 0.3 }
    );

    const currentRef = movementRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasTyped, fullText]);

  return (
    <>
      <div className="home-page">
        <div className="App home-hero">
          <h1 className="title">$BWOB</h1>
          <img
            src={bwobMainImage}
            className="bwob-main-image"
            alt="BWOB mascot"
            fetchPriority="high"
            decoding="sync"
          />
          <p className="bwob-description">
            Hello, I'm BWOB the axolotl. The cats, dogs and fwogs have had their fun.
          </p>

          <div className="home-cta" role="navigation" aria-label="BWOB links">
            <LaunchLinkPill
              label="Pump.fun"
              href={PUMP_FUN_URL}
              ariaActive="Open BWOB on Pump.fun"
              ariaSoon="Pump.fun link coming soon"
            />
            <LaunchLinkPill
              label="Dexscreener"
              href={DEXSCREENER_URL}
              ariaActive="Open BWOB on Dexscreener"
              ariaSoon="Dexscreener link coming soon"
            />

            <div
              className="contract-address-pill home-cta__item"
              title={hasAddress ? SOLANA_CONTRACT_ADDRESS : undefined}
            >
              <span className="contract-address-pill__label">Contract</span>
              <span className="contract-address-pill__value">
                {hasAddress ? shortenContract(SOLANA_CONTRACT_ADDRESS.trim()) : 'Coming soon'}
              </span>
              <button
                type="button"
                className="contract-address-pill__copy"
                onClick={onCopyContract}
                disabled={!hasAddress}
                aria-label={hasAddress ? 'Copy contract address' : 'Contract address not set yet'}
              >
                {copied ? (
                  <span className="contract-address-pill__copied">Copied</span>
                ) : (
                  <svg className="contract-address-pill__icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden>
                    <path
                      fill="currentColor"
                      d="M16 1H4c-1.1 0-2 .9-2 2v12h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
                    />
                  </svg>
                )}
              </button>
            </div>

            <a
              href="https://x.com/justabwob"
              target="_blank"
              rel="noopener noreferrer"
              className="build-bwob-button home-cta__item twitter-button"
              aria-label="Twitter @justabwob"
            >
              Twitter
            </a>
            <a
              href="https://t.me/bwobonsol"
              target="_blank"
              rel="noopener noreferrer"
              className="build-bwob-button home-cta__item"
              aria-label="Telegram BWOB portal"
            >
              Telegram
            </a>
            <Link to="/build" className="build-bwob-button home-cta__item">
              Build a BWOB
            </Link>
          </div>
        </div>

        <section className="why-bwob" aria-labelledby="why-bwob-heading">
          <h2 className="why-bwob__title" id="why-bwob-heading">
            Why BWOB?
          </h2>
          <div className="why-bwob__box">
            <p className="why-bwob__text">
              We want to bring back fun coins centered around lovable characters. BWOB is here for the vibes.
            </p>
          </div>
        </section>

        <BwobMemeScroller />

        <section className="movement-cta" aria-labelledby="movement-heading" ref={movementRef}>
          <div className="movement-cta__inner">
            <div className="movement-cta__quote-box">
              <div className="movement-cta__quote-glow" aria-hidden />
              <blockquote className="movement-cta__quote">
                <span className="movement-cta__quote-mark" aria-hidden>"</span>
                <p id="movement-heading" className="movement-cta__title">
                  {typewriterText}
                  {hasTyped && typewriterText.length < fullText.length && (
                    <span className="movement-cta__cursor" aria-hidden>|</span>
                  )}
                </p>
                <span className="movement-cta__quote-mark movement-cta__quote-mark--close" aria-hidden>"</span>
              </blockquote>
            </div>
            <figure className="movement-cta__figure">
              <div className="movement-cta__figure-glow" aria-hidden />
              <img
                src={`${process.env.PUBLIC_URL || ''}/bwob-matrix-nobg.png`}
                alt="BWOB dressed as Morpheus offering the red pill and blue pill"
                className="movement-cta__img"
                loading="lazy"
                decoding="async"
              />
            </figure>
          </div>
        </section>
      </div>
    </>
  );
}

export default HomePage;
